import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  LayoutDashboard,
  Users,
  Stethoscope,
  CalendarDays,
  Wallet,
  Banknote,
  FileText,
  ScrollText,
  CalendarRange,
  Calculator,
  Briefcase,
  Receipt,
  PenTool,
  Building2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// Secciones del módulo, agrupadas como en la lateral de parametrización de
// centros. `enabled` se va encendiendo parte a parte: una sección sin backend
// todavía se oculta, en vez de mostrar una pantalla que fallaría al cargar.
// Al llegar su parte se cambia el flag y la lateral ya estámontada.
export const PERSONAL_SECTIONS = [
  {
    title: 'General',
    items: [
      // La portada del módulo es el listado de personal: es lo primero que
      // necesita ver quien entra a administering el equipo.
      { to: '/personal', label: 'Personal', icon: Users, enabled: true, end: true },
      // Administrativos, comerciales y gerencia: no marcan en planta, pero su nómina sí
      // afecta el costo de los centros.
      { to: '/personal/administrativo', label: 'Personal administrativo', icon: Briefcase, enabled: true },
      { to: '/personal/resumen', label: 'Resumen', icon: LayoutDashboard, enabled: true },
    ],
  },
  {
    title: 'Ausencias',
    items: [
      { to: '/personal/incapacidades', label: 'Incapacidades', icon: Stethoscope, enabled: true },
      { to: '/personal/permisos', label: 'Permisos', icon: CalendarDays, enabled: true },
    ],
  },
  {
    title: 'Nómina',
    items: [
      { to: '/personal/nomina', label: 'Nóminas por periodo', icon: Banknote, enabled: true },
      { to: '/personal/nomina-directa', label: 'Nómina directa', icon: Building2, enabled: true },
      { to: '/personal/nomina-pagos', label: 'Pagos de nómina', icon: Wallet, enabled: false },
    ],
  },
  {
    title: 'Documentos',
    items: [
      {
        to: '/personal/documentos',
        label: 'Generar documentos',
        icon: FileText,
        enabled: true,
        end: true,
      },
      { to: '/personal/comunicaciones', label: 'Comunicaciones', icon: ScrollText, enabled: false },
    ],
  },
  // Parametrización propia del módulo: son catálogos que gobiernan la nómina
  // y los documentos, y antes vivían dispersos (festivos dentro de la página
  // de liquidación, multiplicadores solo como estado local).
  {
    title: 'Parametrización',
    items: [
      {
        to: '/personal/parametrizacion',
        label: 'General',
        icon: Building2,
        enabled: true,
        end: true,
      },
      {
        to: '/personal/parametrizacion/festivos',
        label: 'Festivos',
        icon: CalendarRange,
        enabled: true,
      },
      {
        to: '/personal/parametrizacion/recargos',
        label: 'Recargos y multiplicadores',
        icon: Calculator,
        enabled: true,
      },
      {
        to: '/personal/parametrizacion/conceptos',
        label: 'Conceptos de nómina',
        icon: Receipt,
        enabled: true,
      },
      {
        to: '/personal/parametrizacion/firmas',
        label: 'Firmas',
        icon: PenTool,
        enabled: true,
      },
    ],
  },
];

const PersonalLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Solo se pintan los grupos que tienen al menos una sección encendida, para
  // no dejar cabeceras de grupo sueltas.
  const groups = PERSONAL_SECTIONS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.enabled),
  })).filter((group) => group.items.length > 0);

  const iconClass = (isActive) =>
    `h-4 w-4 ${isActive ? 'text-primary' : 'text-gray-500'}`;

  const badgeClass = (isActive) =>
    `p-1.5 rounded-md ${isActive ? 'bg-primary/20' : 'bg-gray-100'}`;

  return (
    <div className="space-y-4">


      <div className="flex flex-col lg:flex-row gap-4 min-h-[600px]">
        {/* Lateral del módulo */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className={`w-full shrink-0 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:w-14' : 'lg:w-72'
          }`}
        >
          <Card className="h-full">
            <CardContent className={`p-3 relative ${sidebarCollapsed ? 'px-1' : ''}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                {!sidebarCollapsed && (
                  <h2 className="text-sm font-semibold text-gray-700">Secciones</h2>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed((prev) => !prev)}
                  title={sidebarCollapsed ? 'Expandir secciones' : 'Ocultar secciones'}
                  className={`h-9 w-9 ${sidebarCollapsed ? 'mx-auto' : ''}`}
                >
                  {sidebarCollapsed ? (
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ChevronLeft className="h-4 w-4 text-gray-600" />
                  )}
                </Button>
              </div>

              <div className="space-y-4">
                {groups.map((group) => (
                  <div key={group.title}>
                    {!sidebarCollapsed && (
                      <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1.5">
                        {group.title}
                      </h3>
                    )}
                    <div className="space-y-0.5">
                      {group.items.map((item) => {
                        const Icon = item.icon;

                        // Colapsada: solo el ícono, con el label como tooltip.
                        if (sidebarCollapsed) {
                          return (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              end={item.end}
                              title={item.label}
                              className={({ isActive }) =>
                                `w-full flex items-center justify-center p-2 rounded-lg transition-all ${
                                  isActive
                                    ? 'bg-primary/20 shadow-sm'
                                    : 'hover:bg-gray-50 text-gray-600'
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <div className={badgeClass(isActive)}>
                                  <Icon className={iconClass(isActive)} />
                                </div>
                              )}
                            </NavLink>
                          );
                        }

                        return (
                          <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) =>
                              `w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                                isActive
                                  ? 'bg-primary/20 text-primary font-semibold shadow-sm'
                                  : 'hover:bg-gray-50 text-gray-600'
                              }`
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <div className={badgeClass(isActive)}>
                                  <Icon className={iconClass(isActive)} />
                                </div>
                                <span className="truncate">{item.label}</span>
                              </>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contenido de la sección activa */}
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default PersonalLayout;